import { createStaticMapWithLatLng } from '@/commonFunctions/utils'
import { Navigation } from '@/components/icons'
import { FLEX_ELIMINATION } from '@/constants/Common'
import { ROUTE_CLUBS } from '@/constants/Routes'
import { Bracket, Event, EventFormat } from '@/types'
import { max, min } from 'lodash'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useMediaQuery, useWindowSize } from 'usehooks-ts'

type Props = {
  event: Event
  bracket: Bracket
}

export const BracketInfo = ({ event, bracket }: Props) => {
  const { t } = useTranslation()

  const isMobile = useMediaQuery('(max-width: 767px)')
  const { width } = useWindowSize()

  const bracketPrincyArray = [bracket?.memberFee, bracket?.nonMemberFee]
  const eventPricingArray = [event?.memberFee || 0, event?.nonMemberFee || 0]
  const currencySymbol = event?.currencyDetails?.currencySymbol

  function getDataRangeBracket(data?: number[]) {
    const minData = data?.[0]
    const maxData = data?.[1]
    return `${minData} - ${maxData}`
  }

  function getDateRange(dateRange: string[]) {
    const startDate = moment(dateRange?.[0]).format('MMMM DD, YYYY hh:mm A')
    const endDate = moment(dateRange?.[1]).format('MMMM DD, YYYY hh:mm A')
    return `${startDate} - ${endDate}`
  }

  function getBacketFormatInfo(playerGroup?: string, format?: EventFormat) {
    switch (playerGroup) {
      case 'MEN':
        if (format === 'DOUBLES') {
          return `${playerGroup} ${format} : ${t('event.menDoubles')}`
        }
        return `${playerGroup} ${format}`
      case 'WOMEN':
        if (format === 'DOUBLES') {
          return `${playerGroup} ${format} : ${t('event.womenDoubles')}`
        }
        return `${playerGroup} ${format}`
      case 'MIXED':
        if (format === 'DOUBLES') {
          return `${playerGroup} ${format} : ${t('event.mixedDoubles')}`
        }
        return `${playerGroup} ${format}`
      case 'OPEN':
        if (format === 'DOUBLES') {
          return `${playerGroup} ${format} : ${t('event.openDoubles')}`
        }
        return `${playerGroup} ${format}`
      default:
        return `${playerGroup} ${format}`
    }
  }

  const redirectToMap = (latitude?: number, longitude?: number) => {
    if (latitude && longitude) {
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`
      window.open(url, '_blank')
    }
  }

  return (
    <div className="md:w-1/3 w-full">
      <div className="bg-white rounded-2xl">
        <img
          src={createStaticMapWithLatLng({
            w: isMobile ? 400 : width / 5,
            lat: event?.address?.latitude,
            lng: event?.address?.longitude,
          })}
          className="w-full rounded-t-2xl"
          alt=""
        />

        <div className="flex flex-col p-4 gap-y-1">
          <Link
            to={`${ROUTE_CLUBS}/${event?.clubId}`}
            className="text-navy-900 text-opacity-80 font-semibold text-base max-w-[192px] md:w-full"
          >
            {event?.clubName}
          </Link>
          <span className="text-navy-900 text-opacity-50 text-sm">{bracket?.leagueAddress?.shortAddress}</span>
          <button
            className="flex bg-primary-500 items-center py-2 px-4 rounded-full gap-x-1 text-white text-sm font-semibold w-28 my-2"
            onClick={() => redirectToMap(event?.address?.latitude, event?.address?.longitude)}
          >
            <Navigation className="text-white" />
            {t('common.navigate')}
          </button>
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('event.pricing')}</h3>
          <p className="text-navy-900 text-opacity-70 text-sm">{t('event.pricingDesc')}</p>
          <div>
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.eventEntryFee')}:&nbsp;</strong>
              {`${currencySymbol}${parseFloat(min(eventPricingArray) as any).toFixed(
                2
              )} - ${currencySymbol}${parseFloat(max(eventPricingArray) as any).toFixed(2)}`}
            </p>
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.bracketPrice')}:&nbsp;</strong>
              {`${currencySymbol}${parseFloat(min(bracketPrincyArray) as any).toFixed(
                2
              )} - ${currencySymbol}${parseFloat(max(bracketPrincyArray) as any).toFixed(2)}`}
            </p>
          </div>
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('event.importantDates')}</h3>
          <p className="text-navy-900 text-opacity-70 text-sm">{t('event.importantDatesDesc')}</p>
          <div className="flex flex-col gap-y-2">
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('common.timeZone')}:</strong>
              <br />
              {bracket?.zoneName ?? 'N/A'}
            </p>
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.registrationDates')}:</strong>
              <br />
              {getDateRange((bracket?.registrationDateTime as string[]) ?? bracket?.registrationDate)}
            </p>
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.competitionDates')}:</strong>
              <br />
              {getDateRange((bracket?.durationDateTime as string[]) ?? bracket?.duration)}
            </p>
          </div>
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('event.registrationLimit')}</h3>
          <div>
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.numberTeamsPermitted')}:</strong>
              <br />
              {bracket?.maxTeam} {t('event.teams')}
            </p>
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.waitlistAllowance')}:</strong>
              <br />
              {bracket?.waitList} {t('event.allowedWaitlist')}
            </p>
          </div>
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('event.bracketType')}</h3>
          {bracket?.elimination === 'ROUND_ROBIN' ? (
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.roundRobin')}:</strong>
              &nbsp;{t('event.roundRobinDesc')}
            </p>
          ) : bracket?.elimination === 'COMPASS' ? (
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.waterfall')}:</strong>
              &nbsp;{t('event.waterfallDesc')}
            </p>
          ) : null}
          {bracket?.elimination === FLEX_ELIMINATION && (
            <p className="text-navy-900 text-opacity-70 text-sm">
              <strong>{t('event.flexLeague')}:</strong>
              &nbsp;{t('event.flexLeagueDesc')}
            </p>
          )}
        </div>
        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('event.numberCourts')}</h3>
          <p className="text-navy-900 text-opacity-70 text-sm">{bracket?.courts === 0 ? 'N/A' : bracket?.courts}</p>
        </div>
        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('event.playerGroup')}</h3>
          <p className="text-navy-900 text-opacity-70 text-sm">
            {getBacketFormatInfo(bracket?.playerGroup, bracket?.format)}
          </p>
        </div>
        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('event.playerRules')}</h3>
          <p className="text-navy-900 text-opacity-70 text-sm">
            <strong>{t('event.skillLevel')}:&nbsp;</strong>
            {getDataRangeBracket(bracket?.ratingBracket)}
          </p>
          <p className="text-navy-900 text-opacity-70 text-sm">
            <strong>{t('event.ageGroup')}:&nbsp;</strong>
            {getDataRangeBracket(bracket?.ageBracket)}
          </p>
        </div>
      </div>
    </div>
  )
}
